import { useEffect, useState } from "react";
import { api, BackupHistoryEntry } from "../api";
import { GlassPanel, Badge, SkeletonCard, ErrorState, toast } from "../components";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function BackupPage() {
  const [loading, setLoading] = useState(false);
  const [dataSize, setDataSize] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [history, setHistory] = useState<BackupHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const r = await api.ops.backup.history(50);
      setHistory(r.history || []);
    } catch (e: unknown) {
      setHistoryError((e as Error).message);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const downloadAllData = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await api.ops.backup.run();
      const jsonStr = JSON.stringify(r.data, null, 2);
      setDataSize(formatSize(r.size_bytes || new Blob([jsonStr]).size));

      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `loyverse-backup-${today}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess("Tải backup thành công");
      toast.success("Đã backup");
      loadHistory();
    } catch (e: unknown) {
      const msg = (e as Error).message || "Không thể tải dữ liệu";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Section: Backup thủ công ── */}
      <GlassPanel title="Backup thủ công" badge="export" badgeVariant="info">
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
          Tải toàn bộ dữ liệu (sản phẩm, chi phí, công nợ, ký gửi, todos, backfill, tài sản) về máy dạng JSON.
        </p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <button
            className="btn btn-primary"
            disabled={loading}
            onClick={downloadAllData}
          >
            {loading ? (
              <><span className="spinner" /> Đang tải...</>
            ) : (
              <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span> Tải toàn bộ data (.json)</>
            )}
          </button>

          <button
            className="btn btn-outline"
            disabled
            title="Sắp có — đang phát triển endpoint backup lên Google Sheet"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>cloud_upload</span> Backup lên Sheet
          </button>
        </div>

        {dataSize && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-secondary)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "middle" }}>storage</span>{" "}
            Kích thước data: <strong style={{ color: "var(--text-primary)" }}>{dataSize}</strong>
          </div>
        )}

        {success && (
          <div className="alert alert-ok" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined">check_circle</span> {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined">error</span> {error}
          </div>
        )}
      </GlassPanel>

      {/* ── Section: Lịch sử backup ── */}
      <GlassPanel
        title="Lịch sử backup"
        badge={`${history.length} bản`}
        badgeVariant="info"
        style={{ marginTop: 16 }}
      >
        {historyLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} height={56} />)}
          </div>
        ) : historyError ? (
          <ErrorState error={historyError} onRetry={loadHistory} />
        ) : history.length === 0 ? (
          <div className="empty" style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)" }}>
            Chưa có bản backup nào. Nhấn nút phía trên để tạo backup đầu tiên.
          </div>
        ) : (
          <div className="list">
            {history.map((entry) => (
              <div key={entry.id} className="list-item">
                <div>
                  <div className="name">
                    {new Date(entry.timestamp).toLocaleString("vi-VN")}
                  </div>
                  <div className="meta">
                    {entry.target === "sheet" ? "Google Sheet" : "JSON download"} —{" "}
                    {entry.size_bytes ? formatSize(entry.size_bytes) : "—"}
                    {entry.counts && Object.keys(entry.counts).length > 0 && (
                      <span style={{ marginLeft: 8, opacity: 0.7 }}>
                        ({Object.entries(entry.counts).map(([k, v]) => `${k}: ${v}`).join(", ")})
                      </span>
                    )}
                  </div>
                </div>
                <div className="right">
                  <Badge variant={entry.status === "success" ? "ok" : "bad"}>
                    {entry.status === "success" ? "Thành công" : "Thất bại"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      {/* ── Section: Cấu hình cron ── */}
      <GlassPanel title="Cấu hình cron" badge="readonly" badgeVariant="" style={{ marginTop: 16 }}>
        <div className="list">
          <div className="list-item">
            <div>
              <div className="name">Daily 23:00 → Google Sheet</div>
              <div className="meta">Archive vào sheet Archive_YYYY_MM tự động mỗi đêm</div>
            </div>
            <div className="right">
              <span className="material-symbols-outlined" style={{ color: "var(--text-tertiary)" }}>schedule</span>
            </div>
          </div>
          <div className="list-item">
            <div>
              <div className="name">Cleanup D1 02:00</div>
              <div className="meta">Xoá data &gt;30 ngày trong D1, chỉ chạy sau khi backup OK</div>
            </div>
            <div className="right">
              <span className="material-symbols-outlined" style={{ color: "var(--text-tertiary)" }}>delete_sweep</span>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 12, fontStyle: "italic" }}>
          Cron đã cấu hình trong wrangler.toml, không thay đổi qua UI.
        </p>
      </GlassPanel>
    </>
  );
}
